import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OrderDetailProduct {
  id: string;
  producttitle: string | null;
  varianttitle: string | null;
  sku: string | null;
  artikel_code: string | null;
  ean: string | null;
  quantityordered: number | null;
  quantity_shipped: number | null;
  prijs_incl_btw: number | null;
  customfieldcolor: string | null;
  suppliertitle: string | null;
  brand_title: string | null;
  image_url: string | null;
}

export interface OrderDetailInfo {
  ordernumber: string;
  status: string | null;
  createdat: string | null;
  voornaam_klant: string | null;
  achternaam_klant: string | null;
  emailadres_klant: string | null;
  telefoonnummer_klant: string | null;
  opmerking_klant: string | null;
  addressshippingstreet: string | null;
  addressshippingnumber: string | null;
  addressshippingzipcode: string | null;
  addressshippingcity: string | null;
  verzendadres_land: string | null;
  verzendmethode: string | null;
}

export interface OrderDetail {
  orderInfo: OrderDetailInfo;
  products: OrderDetailProduct[];
  totalPrice: number;
  totalItems: number;
}

export const useOrderDetail = (ordernumber: string | null) => {
  return useQuery({
    queryKey: ["order-detail", ordernumber],
    queryFn: async (): Promise<OrderDetail | null> => {
      if (!ordernumber) return null;

      const { data, error } = await supabase
        .from("orders_live_ls")
        .select(`
          id,
          ordernumber,
          status,
          createdat,
          producttitle,
          varianttitle,
          sku,
          artikel_code,
          ean,
          quantityordered,
          quantity_shipped,
          prijs_incl_btw,
          customfieldcolor,
          suppliertitle,
          brand_title,
          image_url,
          voornaam_klant,
          achternaam_klant,
          emailadres_klant,
          telefoonnummer_klant,
          opmerking_klant,
          addressshippingstreet,
          addressshippingnumber,
          addressshippingzipcode,
          addressshippingcity,
          verzendadres_land,
          verzendmethode
        `)
        .eq("ordernumber", ordernumber);

      if (error) {
        console.error("[useOrderDetail] Error:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Extract order info from first row
      const firstRow = data[0];
      const orderInfo: OrderDetailInfo = {
        ordernumber: firstRow.ordernumber,
        status: firstRow.status,
        createdat: firstRow.createdat,
        voornaam_klant: firstRow.voornaam_klant,
        achternaam_klant: firstRow.achternaam_klant,
        emailadres_klant: firstRow.emailadres_klant,
        telefoonnummer_klant: firstRow.telefoonnummer_klant,
        opmerking_klant: firstRow.opmerking_klant,
        addressshippingstreet: firstRow.addressshippingstreet,
        addressshippingnumber: firstRow.addressshippingnumber,
        addressshippingzipcode: firstRow.addressshippingzipcode,
        addressshippingcity: firstRow.addressshippingcity,
        verzendadres_land: firstRow.verzendadres_land,
        verzendmethode: firstRow.verzendmethode,
      };

      // Map all products
      const products: OrderDetailProduct[] = data.map(row => ({
        id: row.id,
        producttitle: row.producttitle,
        varianttitle: row.varianttitle,
        sku: row.sku,
        artikel_code: row.artikel_code,
        ean: row.ean,
        quantityordered: row.quantityordered,
        quantity_shipped: row.quantity_shipped,
        prijs_incl_btw: row.prijs_incl_btw,
        customfieldcolor: row.customfieldcolor,
        suppliertitle: row.suppliertitle,
        brand_title: row.brand_title,
        image_url: row.image_url,
      }));

      // Deduplicate products with same sku + varianttitle + customfieldcolor
      // Two sources write to orders_live_ls: our edge function (full data) and an external app (partial data).
      // We keep the richest record and use Math.max for quantities (not sum, since they represent the same items).
      const dedupeMap = new Map<string, OrderDetailProduct>();
      for (const p of products) {
        const key = `${p.sku}||${p.varianttitle}||${p.customfieldcolor}`;
        const existing = dedupeMap.get(key);
        if (existing) {
          existing.quantityordered = Math.max(existing.quantityordered || 0, p.quantityordered || 0);
          existing.quantity_shipped = Math.max(existing.quantity_shipped || 0, p.quantity_shipped || 0);
          existing.prijs_incl_btw = Math.max(existing.prijs_incl_btw || 0, p.prijs_incl_btw || 0);
          if (!existing.image_url && p.image_url) existing.image_url = p.image_url;
          if (!existing.suppliertitle && p.suppliertitle) existing.suppliertitle = p.suppliertitle;
          if (!existing.brand_title && p.brand_title) existing.brand_title = p.brand_title;
          if (!existing.producttitle && p.producttitle) existing.producttitle = p.producttitle;
        } else {
          dedupeMap.set(key, { ...p });
        }
      }
      const dedupedProducts = Array.from(dedupeMap.values());

      // Calculate totals
      const totalPrice = dedupedProducts.reduce((sum, p) => sum + (p.prijs_incl_btw || 0), 0);
      const totalItems = dedupedProducts.reduce((sum, p) => sum + (p.quantityordered || 0), 0);

      console.log(`[useOrderDetail] Loaded order ${ordernumber}: ${products.length} lines deduped to ${dedupedProducts.length}`);

      return {
        orderInfo,
        products: dedupedProducts,
        totalPrice,
        totalItems,
      };
    },
    enabled: !!ordernumber,
    staleTime: 30 * 1000,
  });
};
